import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { ChevronLeftIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Link } from '@restworld/ui-ds-base';
import {
  SectionTitle,
  SectionTitleText,
  SectionSubtitle,
  SummaryItemsContainer,
  JobDot,
  SummaryItem,
  SummaryItemInfo,
  SummaryItemDate,
  SummaryItemStats,
  SummaryItemDays,
  SummaryItemDaysLabel,
  SummaryItemCost,
  PaginationContainer,
  PaginationInfo,
  PaginationButtons,
  PaginationButton,
} from './styled';
import { formatPublishDate } from './utils';
import type { JobStats } from './types';

const PAGE_SIZE = 5;

interface AllTimeSummaryProps {
  allJobStats: JobStats[];
  jobColorMap: Record<string, string>;
}

const AllTimeSummary: React.FC<AllTimeSummaryProps> = ({ allJobStats, jobColorMap }) => {
  const { t } = useTranslation('payment-and-invoices');
  const monthsAbbr = t('months_abbr', { returnObjects: true }) as string[];
  const [page, setPage] = React.useState(0);

  const totalPages = Math.ceil(allJobStats.length / PAGE_SIZE);
  const pagedJobs = allJobStats.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
      <SectionTitle>
        <div>
          <SectionTitleText>{t('summary_by_position')}</SectionTitleText>
          <div>
            <SectionSubtitle>{t('total_from_start')}</SectionSubtitle>
          </div>
        </div>
      </SectionTitle>

      <SummaryItemsContainer>
        {pagedJobs.map((job, idx) => {
          const color = jobColorMap[job.jp_id];
          return (
            <SummaryItem key={job.jp_id} hasBorder={idx < pagedJobs.length - 1}>
              <JobDot color={color} className="self-start mt-3" />
              <SummaryItemInfo>
                <Link
                  href={`/employer/job-positions/${job.jp_id}`}
                  newTab
                  size="sm"
                  variant="neutral"
                  EndIcon={ArrowTopRightOnSquareIcon}
                  className={'w-full truncate font-semibold text-neutral-900'}
                >
                  {job.title}
                </Link>
                <SummaryItemDate>
                  {t('published_on')} {formatPublishDate(job.firstDate, monthsAbbr)}
                </SummaryItemDate>
              </SummaryItemInfo>
              <SummaryItemStats>
                <SummaryItemDays>
                  {job.days} <SummaryItemDaysLabel>{t('days_short')}</SummaryItemDaysLabel>
                </SummaryItemDays>
                <SummaryItemCost>&euro;{job.cost.toFixed(2)}</SummaryItemCost>
              </SummaryItemStats>
            </SummaryItem>
          );
        })}
      </SummaryItemsContainer>

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            {page * PAGE_SIZE + 1}&ndash;{Math.min((page + 1) * PAGE_SIZE, allJobStats.length)} di {allJobStats.length}
          </PaginationInfo>
          <PaginationButtons>
            <PaginationButton
              isDisabled={page === 0}
              disabled={page === 0}
              onClick={() => setPage(Math.max(0, page - 1))}
            >
              <ChevronLeftIcon className="w-3.5 h-3.5 text-neutral-700" />
            </PaginationButton>
            <PaginationButton
              isDisabled={page === totalPages - 1}
              disabled={page === totalPages - 1}
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            >
              <ChevronRightIcon className="w-3.5 h-3.5 text-neutral-700" />
            </PaginationButton>
          </PaginationButtons>
        </PaginationContainer>
      )}
    </>
  );
};

export default AllTimeSummary;
